import { Code, CodeType, TrimmedCode } from "types/entities/Code";
import { MatchGroup } from "types/entities/Match";

// What a taxonomy item's codes are compared with on the matching screen:
// the selected rows' source codes and their source description text
export type MatchSource = {
  codes: TrimmedCode[];
  texts: string[];
};

// Taxonomy code types compared on the matching screen, and the source code types each one is compared with.
// The pipeline only writes EAN (provider barcode) and CLINIC (provider product code) source codes,
// so REFID, BARSOI (Germany) and SKU (USA) are compared with CLINIC codes
const SOURCE_TYPES_BY_CODE_TYPE: Partial<Record<CodeType, string[]>> = {
  [CodeType.EAN]: [CodeType.EAN],
  [CodeType.REFID]: [CodeType.REFID, CodeType.CLINIC],
  [CodeType.BARSOI]: [CodeType.BARSOI, CodeType.CLINIC],
  [CodeType.SKU]: [CodeType.SKU, CodeType.CLINIC],
};

// Shorter codes aren't looked for in the description text: "100" would hit "100 ml"
const MIN_TEXT_MATCH_LENGTH = 4;

// EANs are compared without leading zeros: the pipeline strips them from the source EAN,
// while the catalog may hold the 14-digit form (03411112119912 vs 3411112119912)
const normalizeCode = (type: string, code = ""): string => {
  const trimmedCode = code.trim();
  return type === CodeType.EAN ? trimmedCode.replace(/^0+/, "") : trimmedCode;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The code written as a whole word (not inside a longer word or number), ignoring case;
// an EAN may be written with leading zeros
const isCodeInText = (type: string, value: string, text: string): boolean => {
  const leadingZeros = type === CodeType.EAN ? "0*" : "";
  return new RegExp(`(^|[^a-z0-9])${leadingZeros}${escapeRegExp(value)}($|[^a-z0-9])`, "i").test(text);
};

// True when a taxonomy item's EAN, REFID, BARSOI or SKU equals a source code of a comparable type,
// or is written in the source description text
export const isCodeMatch = (code: TrimmedCode, { codes, texts }: MatchSource): boolean => {
  const sourceTypes = SOURCE_TYPES_BY_CODE_TYPE[code.type];
  const value = normalizeCode(code.type, code.code);

  if (!sourceTypes || !value) return false;

  return (
    codes.some((sourceCode) =>
      sourceTypes.includes(sourceCode.type) && normalizeCode(code.type, sourceCode.code) === value
    ) ||
    (value.length >= MIN_TEXT_MATCH_LENGTH && texts.some((text) => isCodeInText(code.type, value, text)))
  );
};

export const excludeCodeTypes = <T extends Code | TrimmedCode>(codes: T[], types: string[]): T[] =>
  codes.filter(({ type }) => !types.includes(type));

// The given rows' codes (metadata.codes.standard, written by the matching pipeline) and description text
export const getMatchSource = (groups: MatchGroup[]): MatchSource => ({
  codes: groups.flatMap((group) => group.metadata_.metadata?.codes?.standard || []),
  texts: groups.map((group) => group.match_text).filter(Boolean),
});
